import React, { useEffect, useState, useMemo } from 'react';
import { Button, Tabs, Select, Modal, Tooltip } from 'antd';
import message from '@web-common/components/UI/Message/SiriusMessage';
import { api, apiHolder, apis, ProductTagEnum, ProductAuthorityFeature } from 'api';
import { useAppDispatch, useAppSelector } from '@web-common/state/createStore';
import { actions as DiskAttActions, doAddDiskAttachment, doGetAttsAsync, doGetRootInfoAsync, selectedTotalSizeOver100M, } from '@web-common/state/reducer/diskAttReducer';
import { DiskAttachmentList } from './DiskAttachmentList';
import { ModalCloseSmall } from '@web-common/components/UI/Icons/icons';
import style from './index.module.scss';
import ProductAuthTag from '@web-common/components/UI/ProductAuthTag/ProductAuthTag';
import IconCard from '@web-common/components/UI/IconCard';
import { useGetProductAuth } from '@web-common/hooks/useGetProductAuth';
import { comIsShowByAuth, getIfHaveAuth } from '@web-common/utils/utils';
import { getIn18Text } from 'api';
const trackApi = apiHolder.api.requireLogicalApi(apis.dataTrackerApiImp);
const diskApi = apiHolder.api.requireLogicalApi(apis.netStorageImpl);
const { TabPane } = Tabs;
const { Option } = Select;
const iconStyle = { width: 15, height: 15, marginRight: 8, position: 'relative', top: 3 };
const XUQI_TEXT = getIn18Text('XUQI');
export const DiskAttachmentModal = () => {
    const dispatch = useAppDispatch();
    const { diskModalVisible, rootInfo, currentType, tabMsgs, selectedRows, listLoading } = useAppSelector(state => state.diskAttReducer);
    const [firstIn, setFirstIn] = useState(true);
    const list = useMemo(() => {
        return tabMsgs[currentType].list || [];
    }, [tabMsgs, currentType]);
    const isLocked = useMemo(() => {
        return tabMsgs['personal'].isLocked || false;
    }, [tabMsgs]);
    const isTotalSizeOver100M = useAppSelector(selectedTotalSizeOver100M);
    const systemApi = api.getSystemApi();
    const mailId = useAppSelector(state => { var _a, _b; return (_b = (_a = state === null || state === void 0 ? void 0 : state.mailReducer) === null || _a === void 0 ? void 0 : _a.currentMail) === null || _b === void 0 ? void 0 : _b.cid; });
    const [attachmentType, setAttachmentType] = useState('normal');
    const [normalDisabled, setNormalDisabled] = useState(false);
    const { productVersionInfo: { productVersionId }, } = useGetProductAuth();
    const cloudDisabled = useMemo(() => currentType === 'normalAtt', [currentType]);
    const over100Remark = useMemo(() => {
        if (currentType === 'normalAtt') {
            return getIn18Text('TIANJIAZIWANGLAI');
        }
        const cloudSwitchOpen = getIfHaveAuth(ProductAuthorityFeature.ORG_SETTING_BIG_ATTACH_SHOW);
        if (cloudSwitchOpen) {
            return getIn18Text('YOUYUXUANZHONGDE');
        }
        return getIn18Text('TIANJIADEWENJIANZONGDAXIAO');
    }, [currentType]);
    const closeModal = () => dispatch(DiskAttActions.doToggleDiskModal(false));
    const disableAdd = useMemo(() => {
        return ((list === null || list === void 0 ? void 0 : list.length) === 0 ||
            selectedRows.length === 0 ||
            (currentType === 'normalAtt' && isTotalSizeOver100M) ||
            (['personal', 'ent', 'cloud'].includes(currentType) && isTotalSizeOver100M && !getIfHaveAuth(ProductAuthorityFeature.ORG_SETTING_BIG_ATTACH_SHOW)));
    }, [list, selectedRows, listLoading, isTotalSizeOver100M, currentType]);
    const disableRenew = useMemo(() => {
        return !(productVersionId === 'ultimate' && currentType === 'cloudAtt' && (list === null || list === void 0 ? void 0 : list.length) > 0 && (selectedRows === null || selectedRows === void 0 ? void 0 : selectedRows.length) > 0 && !listLoading);
    }, [list.length, selectedRows.length, listLoading, currentType, productVersionId]);
    const tabChange = (type) => {
        dispatch(DiskAttActions.doSwitchType(type));
    };
    useEffect(() => {
        if (firstIn)
            return;
        if (!diskModalVisible) {
            dispatch(DiskAttActions.doResetData());
        }
        dispatch(doGetRootInfoAsync());
        setAttachmentType('normal');
    }, [diskModalVisible]);
    useEffect(() => {
        if (firstIn) {
            dispatch(doGetRootInfoAsync());
            dispatch(doGetAttsAsync({ type: 'cloudAtt' }));
            dispatch(doGetAttsAsync({ type: 'normalAtt' }));
            setFirstIn(false);
        }
    }, []);
    useEffect(() => {
        if (isTotalSizeOver100M && getIfHaveAuth(ProductAuthorityFeature.ORG_SETTING_BIG_ATTACH_SHOW)) {
            setAttachmentType('cloud');
            setNormalDisabled(true);
        }
        else {
            setAttachmentType('normal');
            setNormalDisabled(false);
        }
    }, [isTotalSizeOver100M]);
    const handleAddAttachments = async () => {
        trackApi.track('pcMail_click_docsAttachments_writeMailPage', { spaceTab: currentType });
        mailId && dispatch(doAddDiskAttachment({ files: selectedRows, addType: attachmentType, mailId }));
    };
    const renewal = async () => {
        const identities = [];
        selectedRows.forEach(item => {
            const { identity } = item;
            identity && identities.push(identity);
        });
        if (identities.length < 1) {
            console.log('无云附件选中');
            return;
        }
        try {
            const res = await diskApi.renewAttachments({ identities });
            if (res === true) {
                message.success({
                    icon: React.createElement(IconCard, { type: "saved", stroke: "#4C6AFF", style: iconStyle }),
                    content: getIn18Text('XUQICHENGGONG'),
                });
                dispatch(DiskAttActions.doSetSelectedKeys([]));
                if (currentType === 'cloudAtt') {
                    dispatch(doGetAttsAsync({ type: 'cloudAtt', init: true }));
                }
            }
            else {
                message.error({
                    icon: React.createElement(IconCard, { type: "info", stroke: "#FE5B4C", style: iconStyle }),
                    content: getIn18Text('XUQISHIBAIQINGCHONGSHI'),
                });
            }
        }
        catch (err) {
            console.log('续期失败', err);
            message.error({
                icon: React.createElement(IconCard, { type: "info", stroke: "#FE5B4C", style: iconStyle }),
                content: getIn18Text('XUQISHIBAIQINGCHONGSHI'),
            });
        }
    };
    const renderModalContent = () => {
        if (isLocked && currentType === 'personal') {
            return (React.createElement("div", { className: style.empty },
                React.createElement("div", { className: "sirius-empty sirius-empty-doc" }),
                React.createElement("div", { className: style.emptyText },
                    React.createElement("p", null, getIn18Text('GERENKONGJIANYI')),
                    React.createElement("p", null, getIn18Text('DANGQIANBANBENZAN')),
                    React.createElement("p", { style: { marginTop: 12 } }, getIn18Text('KEQIANWANGJIUBAN')),
                    React.createElement("p", null,
                        React.createElement("a", { onClick: () => systemApi === null || systemApi === void 0 ? void 0 : systemApi.openNewWindow('http://qiye.163.com/login') }, "qiye.163.com/login")),
                    React.createElement("p", null, getIn18Text('GUANBIANQUANSUO')))));
        }
        return React.createElement(DiskAttachmentList, null);
    };
    const renderModalFooter = () => (React.createElement("div", { className: style.modalFooter },
        React.createElement("div", { className: style.leftCont },
            !cloudDisabled && (React.createElement("div", { className: style.attTyArea },
                getIn18Text('TIANJIAWEI'),
                React.createElement(Select, { className: style.attTySelect, onChange: value => setAttachmentType(value), value: attachmentType, style: { width: 120 }, bordered: false, dropdownClassName: style.attTyDropdown, suffixIcon: React.createElement(IconCard, { className: "dark-invert", type: "downTriangle" }) },
                    React.createElement(Option, { className: style.attTyOpt, disabled: normalDisabled, value: "normal" }, getIn18Text('PUTONGFUJIAN')),
                    comIsShowByAuth(ProductAuthorityFeature.ORG_SETTING_BIG_ATTACH_SHOW, React.createElement(Option, { className: style.attTyOpt, disabled: cloudDisabled, value: "cloud" }, getIn18Text('YUNFUJIAN')))))),
            React.createElement("div", { className: style.remark, hidden: !isTotalSizeOver100M }, over100Remark)),
        React.createElement("div", { className: style.rightButtons },
            React.createElement(Button, { size: "small", onClick: closeModal }, getIn18Text('QUXIAO')),
            currentType === 'cloudAtt' && (React.createElement(React.Fragment, null,
                productVersionId === 'free' && (React.createElement(Tooltip, { title: getIn18Text('MIANFEIBANBUZHICHIXUQI') },
                    React.createElement(Button, { size: "small", disabled: true }, XUQI_TEXT))),
                productVersionId === 'ultimate' && (React.createElement(Button, { size: "small", disabled: disableRenew, onClick: renewal }, XUQI_TEXT)))),
            React.createElement(Button, { onClick: handleAddAttachments, size: "small", type: "primary", disabled: disableAdd }, getIn18Text('TIANJIA')))));
    return (React.createElement(Modal, { wrapClassName: style.diskModal, visible: diskModalVisible, bodyStyle: { overflow: 'hidden' }, width: 760, destroyOnClose: true, closeIcon: React.createElement(ModalCloseSmall, { className: "dark-invert" }), onCancel: closeModal, footer: renderModalFooter() },
        React.createElement("div", null,
            React.createElement("div", null,
                React.createElement("div", { className: style.modalTitle }, React.createElement(ProductAuthTag, { tagName: ProductTagEnum.NETDISK_ATTACHMENT }, getIn18Text('CONGYUNWENDANGTIAN'))),
                React.createElement(Tabs, { className: style.attTabs, defaultActiveKey: currentType, onChange: tabChange },
                    rootInfo.personal && React.createElement(TabPane, { tab: getIn18Text('GERENKONGJIAN'), key: "personal" }),
                    rootInfo.ent && React.createElement(TabPane, { tab: getIn18Text('QIYEKONGJIAN'), key: "ent" }),
                    comIsShowByAuth(ProductAuthorityFeature.ORG_SETTING_BIG_ATTACH_SHOW, React.createElement(TabPane, { tab: getIn18Text('YUNFUJIANKONGJIAN'), key: "cloudAtt" })),
                    React.createElement(TabPane, { tab: getIn18Text('WANGLAIFUJIAN'), key: "normalAtt" }))),
            renderModalContent())));
};
